import React, { FunctionComponent, ReactChild, useContext, useEffect, useMemo, useState } from 'react'
import { Dimensions, useColorScheme } from 'react-native'

import { debounce } from 'lodash-es'
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components/native'

import { FontFamilyMetadata } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/fontFamilyMetadata'
import { ThemeMetadata } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/productType'
import { ThemeAssets } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/assets'
import { AuroraThemeColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/auroraColors'
import { ResponsiveBreakpoints } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/breakpoints'
import { ThemeColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/colors'
import { ComponentStyles } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/componentStyles'
import { spacing, Spacing } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/spacing'
import { Theme } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/theme'
import {
  getProductTypeMetadata,
  getProductTypeSupportedFontFamily,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/themeUtils'

import { resolveAssetPaths } from './resolveAssetPaths'
import { breakpoints as breakpointsConstants } from '../../constants'
import { useAuroraThemeSettings } from '../../hooks/useAuroraThemeSettings'
import { useThemeSettings } from '../../hooks/useThemeSettings'
import { ProductTypeContext } from '../productType/productTypeContext'

export interface ThemeType {
  breakpoints: ResponsiveBreakpoints
  colors: ThemeColors
  assets: ThemeAssets
  spacing: Spacing
  componentStyles: ComponentStyles
  themeType: Theme
  setThemeType: (theme: Theme) => void
  isDarkMode: boolean
  isLyraSelect: boolean
  isAuroraTheme: boolean
  toggleIsAuroraTheme: () => void
  fontFamilyMetadata: FontFamilyMetadata
}

// Aurora-specific theme type with extended color tokens
export interface AuroraThemeType extends Omit<ThemeType, 'colors'> {
  colors: AuroraThemeColors
}

function calculateBreakpoints(width: number, height: number): ResponsiveBreakpoints {
  const { mobileXs, mobileXsHeight, mobileS, mobileM, mobileL, tablet, laptop, desktop, desktopL, mobileXxs } =
    breakpointsConstants
  return {
    isMinWidthMobileXxs: width >= mobileXxs,
    isMinWidthMobileXs: width >= mobileXs,
    isMinHeightMobileXs: height >= mobileXsHeight,
    isMinWidthMobileS: width >= mobileS,
    isMinWidthMobileM: width >= mobileM,
    isMinWidthMobileL: width >= mobileL,
    isMinWidthTablet: width >= tablet,
    isMinWidthLaptop: width >= laptop,
    isMinWidthDesktop: width >= desktop,
    isMinWidthDesktopL: width >= desktopL,
    isMobileSized: width < tablet,
  }
}

export const ThemeProvider: FunctionComponent<{
  children: ReactChild | ReactChild[]
  /**
   * Not needed in App.tsx but needed for Storybook to use the Theme selector
   * in libs/ui-core-crossplatform/.storybook/decorators.js
   */
  theme?: Theme
}> = ({ children, theme: providedTheme }) => {
  const { themeType, setThemeType } = useThemeSettings()
  const { isAuroraThemeEnabled, toggleIsAuroraTheme } = useAuroraThemeSettings()
  const { isLyraSelect, productType, isAuroraEnabled } = useContext(ProductTypeContext)
  const isAuroraTheme = isAuroraThemeEnabled || isAuroraEnabled
  const [breakpoints, setBreakpoints] = useState(
    calculateBreakpoints(Dimensions.get('window').width, Dimensions.get('window').height),
  )

  const colorScheme = useColorScheme()

  useEffect(() => {
    const listener = Dimensions.addEventListener(
      'change',
      debounce(({ window }) => {
        setBreakpoints(calculateBreakpoints(window.width, window.height))
      }, 75),
    )
    return () => listener.remove()
  })

  const isDarkMode = useMemo(() => {
    if (providedTheme) {
      return providedTheme === Theme.DARK
    }

    return themeType === Theme.DARK || (themeType === Theme.SYSTEM && colorScheme === Theme.DARK)
  }, [colorScheme, providedTheme, themeType])

  const colorTheme = isDarkMode ? Theme.DARK : Theme.LIGHT

  const { colors, assets, fontFamilyMetadata, componentStyles } = useMemo(() => {
    const {
      colorTokens,
      assets: themeAssets,
      componentStyles,
    } = getProductTypeMetadata(colorTheme, productType, isAuroraTheme) as ThemeMetadata
    const fontFamilyMetadata = getProductTypeSupportedFontFamily(productType, isAuroraTheme)
    return {
      colors: colorTokens,
      assets: resolveAssetPaths(themeAssets, productType),
      fontFamilyMetadata,
      componentStyles,
    }
  }, [colorTheme, productType, isAuroraTheme])

  return (
    <StyledComponentsThemeProvider
      theme={{
        breakpoints,
        colors,
        assets,
        spacing,
        componentStyles,
        themeType,
        setThemeType,
        isDarkMode,
        isLyraSelect,
        isAuroraTheme,
        toggleIsAuroraTheme,
        fontFamilyMetadata,
      }}
    >
      {children}
    </StyledComponentsThemeProvider>
  )
}
